import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Badge,
  Card,
  Spinner,
} from 'react-bootstrap';
import { FaPlus, FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import WorkflowCriteriaConfig from '../../workflow/WorkflowCriteriaConfig';
import InsightActionsEditor from './InsightActionsEditor';
import { useConditionValueFields } from '../../../hooks/useConditionValueFields';
import { FILTER_VALUE_TYPE_STATIC } from '../../../utils/filterFieldValueUtils';
import { standardInputStyles, standardLabelStyles } from '../../../utils/conditionInputStyles';

const TRIGGER_OPTIONS = [
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'CREATE_OR_UPDATE', label: 'Create or Update' },
];

const SEVERITY_OPTIONS = ['HIGH', 'MEDIUM', 'LOW'];

const emptySignalTracker = () => ({
  trackerId: '',
  object: '',
  trigger: 'UPDATE',
  targetField: '',
  relatedRecord: { objectField: 'related_object', idField: 'related_record_id' },
  logic: { logic: 'AND', customLogic: '', criteria: [] },
  _uiKind: 'signal',
});

const emptyStageTracker = () => ({
  trackerId: '',
  sourceField: '',
  targetField: '',
  trigger: 'UPDATE',
  _uiKind: 'stage',
});

const emptyInsight = () => ({
  name: '',
  trackerId: '',
  days: 15,
  severity: 'HIGH',
  actions: [],
});

function triggerToWorkflowEvent(trigger) {
  switch (String(trigger || '').toUpperCase()) {
    case 'CREATE':
      return 'onCreate';
    case 'DELETE':
      return 'onDelete';
    case 'CREATE_OR_UPDATE':
      return 'onCreate,onUpdate';
    default:
      return 'onUpdate';
  }
}

function isStageTracker(tracker) {
  return Boolean(String(tracker?.sourceField || '').trim());
}

function trackerShowsStageForm(tracker) {
  if (tracker?._uiKind === 'stage') return true;
  if (tracker?._uiKind === 'signal') return false;
  return isStageTracker(tracker);
}

function stripUiFields(config) {
  const trackers = (config?.trackers || []).map(({ _uiKind, ...tracker }) => tracker);
  return { ...config, trackers };
}

function SignalTrackerCriteriaSection({ tracker, trackerIndex, onTrackerChange, objects }) {
  const sourceObject = tracker?.object || '';
  const [sourceFields, setSourceFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const { valueFields: criteriaValueFields } = useConditionValueFields(sourceObject);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!sourceObject) {
        setSourceFields([]);
        return;
      }
      setLoadingFields(true);
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(sourceObject);
        if (!cancelled) {
          setSourceFields(res?.success && res?.data?.fields ? res.data.fields : []);
        }
      } catch {
        if (!cancelled) setSourceFields([]);
      } finally {
        if (!cancelled) setLoadingFields(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sourceObject]);

  const criteriaList = tracker?.logic?.criteria || [];

  const patchTracker = (patch) => {
    onTrackerChange(trackerIndex, { ...tracker, ...patch });
  };

  const patchLogic = (patch) => {
    patchTracker({
      logic: { ...(tracker.logic || {}), ...patch },
    });
  };

  const addCriteria = () => {
    patchLogic({
      criteria: [
        ...criteriaList,
        {
          fieldName: '',
          operator: '',
          value: '',
          valueType: FILTER_VALUE_TYPE_STATIC,
          evaluateOn: 'Always',
        },
      ],
    });
  };

  const deleteCriteria = (index) => {
    patchLogic({ criteria: criteriaList.filter((_, i) => i !== index) });
  };

  const onCriteriaChange = (index, field, value) => {
    patchLogic({
      criteria: criteriaList.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    });
  };

  const onCriteriaPatch = (index, patch) => {
    patchLogic({
      criteria: criteriaList.map((row, i) =>
        i === index ? { ...row, ...patch } : row
      ),
    });
  };

  return (
    <>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Source object</Form.Label>
            <Form.Select
              value={tracker.object || ''}
              onChange={(e) => patchTracker({ object: e.target.value })}
              style={standardInputStyles}
            >
              <option value="">Select object</option>
              {objects.map((obj) => (
                <option key={obj.name} value={obj.name}>
                  {obj.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Trigger</Form.Label>
            <Form.Select
              value={tracker.trigger || 'UPDATE'}
              onChange={(e) => patchTracker({ trigger: e.target.value })}
              style={standardInputStyles}
            >
              {TRIGGER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Target field (on parent object)</Form.Label>
            <Form.Control
              value={tracker.targetField || ''}
              onChange={(e) => patchTracker({ targetField: e.target.value })}
              placeholder="e.g. last_activity_date"
              style={standardInputStyles}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Related object field</Form.Label>
            <Form.Control
              value={tracker.relatedRecord?.objectField || ''}
              onChange={(e) =>
                patchTracker({
                  relatedRecord: {
                    ...(tracker.relatedRecord || {}),
                    objectField: e.target.value,
                  },
                })
              }
              placeholder="related_object"
              style={standardInputStyles}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Related record id field</Form.Label>
            <Form.Control
              value={tracker.relatedRecord?.idField || ''}
              onChange={(e) =>
                patchTracker({
                  relatedRecord: {
                    ...(tracker.relatedRecord || {}),
                    idField: e.target.value,
                  },
                })
              }
              placeholder="related_record_id"
              style={standardInputStyles}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md={4}>
          <Form.Group>
            <Form.Label style={standardLabelStyles}>Criteria logic</Form.Label>
            <Form.Select
              value={tracker.logic?.logic || 'AND'}
              onChange={(e) => patchLogic({ logic: e.target.value })}
              style={standardInputStyles}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
              <option value="Custom">Custom</option>
            </Form.Select>
          </Form.Group>
        </Col>
        {String(tracker.logic?.logic || '').toUpperCase() === 'CUSTOM' && (
          <Col md={8}>
            <Form.Group>
              <Form.Label style={standardLabelStyles}>Custom logic</Form.Label>
              <Form.Control
                value={tracker.logic?.customLogic || ''}
                onChange={(e) => patchLogic({ customLogic: e.target.value })}
                placeholder="(1 AND 2) OR 3"
                style={standardInputStyles}
              />
            </Form.Group>
          </Col>
        )}
      </Row>

      {loadingFields ? (
        <div className="text-muted py-2">
          <Spinner size="sm" className="me-2" />
          Loading source object fields…
        </div>
      ) : (
        <WorkflowCriteriaConfig
          criteriaList={criteriaList}
          onAdd={addCriteria}
          onDelete={deleteCriteria}
          onChange={onCriteriaChange}
          onPatch={onCriteriaPatch}
          objectFields={sourceFields}
          valueFields={criteriaValueFields?.length ? criteriaValueFields : sourceFields}
          eventType={triggerToWorkflowEvent(tracker.trigger)}
          triggerEvent="after_save"
          disabled={!sourceObject}
        />
      )}
    </>
  );
}

export default function ObjectHealthConfigForm({
  show,
  onHide,
  onSave,
  editingConfig,
  objects,
  targetFields: targetFieldsProp = [],
}) {
  const [formData, setFormData] = useState({
    id: null,
    object_name: '',
    is_active: true,
    config: { trackers: [], insights: [] },
  });
  const [targetFields, setTargetFields] = useState(targetFieldsProp);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTargetFields(targetFieldsProp);
  }, [targetFieldsProp]);

  useEffect(() => {
    let cancelled = false;
    const objectName = formData.object_name;
    if (!objectName || !show) {
      return undefined;
    }
    (async () => {
      try {
        const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (!cancelled) {
          setTargetFields(res?.success && res?.data?.fields ? res.data.fields : []);
        }
      } catch {
        if (!cancelled) setTargetFields([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.object_name, show]);

  useEffect(() => {
    if (!show) {
      setError('');
      setSaving(false);
      return;
    }
    if (editingConfig) {
      setFormData({
        id: editingConfig.id,
        object_name: editingConfig.object_name || '',
        is_active: editingConfig.is_active !== false,
        config: {
          trackers: editingConfig.config?.trackers || [],
          insights: editingConfig.config?.insights || [],
        },
      });
    } else {
      setFormData({
        id: null,
        object_name: '',
        is_active: true,
        config: { trackers: [], insights: [] },
      });
    }
  }, [show, editingConfig]);

  const trackerIds = useMemo(
    () => (formData.config.trackers || []).map((t) => t.trackerId).filter(Boolean),
    [formData.config.trackers]
  );

  const updateTracker = (index, tracker) => {
    setFormData((prev) => {
      const trackers = [...(prev.config.trackers || [])];
      trackers[index] = tracker;
      return { ...prev, config: { ...prev.config, trackers } };
    });
  };

  const removeTracker = (index) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        trackers: (prev.config.trackers || []).filter((_, i) => i !== index),
      },
    }));
  };

  const addTracker = (type) => {
    const tracker = type === 'stage' ? emptyStageTracker() : emptySignalTracker();
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        trackers: [...(prev.config.trackers || []), tracker],
      },
    }));
  };

  const updateInsight = (index, patch) => {
    setFormData((prev) => {
      const insights = [...(prev.config.insights || [])];
      insights[index] = { ...insights[index], ...patch };
      return { ...prev, config: { ...prev.config, insights } };
    });
  };

  const removeInsight = (index) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        insights: (prev.config.insights || []).filter((_, i) => i !== index),
      },
    }));
  };

  const addInsight = () => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        insights: [...(prev.config.insights || []), emptyInsight()],
      },
    }));
  };

  const handleSave = async () => {
    setError('');
    if (!formData.object_name) {
      setError('Object is required');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...formData,
        config: stripUiFields(formData.config),
      });
      onHide();
    } catch (err) {
      setError(err?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.id ? 'Edit Object Health Config' : 'New Object Health Config'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label style={standardLabelStyles}>Object</Form.Label>
              <Form.Select
                value={formData.object_name}
                disabled={Boolean(formData.id)}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, object_name: e.target.value }))
                }
                style={standardInputStyles}
              >
                <option value="">Select object</option>
                {objects.map((obj) => (
                  <option key={obj.name} value={obj.name}>
                    {obj.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              id="health-config-active"
              label="Active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
              }
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Trackers</h6>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => addTracker('signal')}>
              <FaPlus className="me-1" />
              Activity tracker
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => addTracker('stage')}>
              <FaPlus className="me-1" />
              Stage tracker
            </Button>
          </div>
        </div>

        {(formData.config.trackers || []).map((tracker, index) => (
          <Card key={`tracker-${index}`} className="mb-3">
            <Card.Body>
              <Row className="mb-2 align-items-center">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label style={standardLabelStyles}>Tracker ID</Form.Label>
                    <Form.Control
                      value={tracker.trackerId || ''}
                      onChange={(e) =>
                        updateTracker(index, { ...tracker, trackerId: e.target.value })
                      }
                      placeholder="activity_tracking"
                      style={standardInputStyles}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Badge bg={trackerShowsStageForm(tracker) ? 'info' : 'primary'}>
                    {trackerShowsStageForm(tracker) ? 'Stage' : 'Activity'}
                  </Badge>
                </Col>
                <Col md={4} className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeTracker(index)}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>

              {trackerShowsStageForm(tracker) ? (
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={standardLabelStyles}>Source field</Form.Label>
                      <Form.Select
                        value={tracker.sourceField || ''}
                        onChange={(e) =>
                          updateTracker(index, {
                            ...tracker,
                            sourceField: e.target.value,
                            object: '',
                          })
                        }
                        style={standardInputStyles}
                      >
                        <option value="">Select field</option>
                        {targetFields.map((f) => (
                          <option key={f.field_name} value={f.field_name}>
                            {f.field_label || f.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={standardLabelStyles}>Target field</Form.Label>
                      <Form.Select
                        value={tracker.targetField || ''}
                        onChange={(e) =>
                          updateTracker(index, { ...tracker, targetField: e.target.value })
                        }
                        style={standardInputStyles}
                      >
                        <option value="">Select field</option>
                        {targetFields.map((f) => (
                          <option key={`tgt-${f.field_name}`} value={f.field_name}>
                            {f.field_label || f.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={standardLabelStyles}>Trigger</Form.Label>
                      <Form.Select
                        value={tracker.trigger || 'UPDATE'}
                        onChange={(e) =>
                          updateTracker(index, { ...tracker, trigger: e.target.value })
                        }
                        style={standardInputStyles}
                      >
                        {TRIGGER_OPTIONS.filter((o) => o.value === 'UPDATE').map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              ) : (
                <SignalTrackerCriteriaSection
                  tracker={tracker}
                  trackerIndex={index}
                  onTrackerChange={updateTracker}
                  objects={objects}
                />
              )}
            </Card.Body>
          </Card>
        ))}

        <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
          <h6 className="mb-0">Insights</h6>
          <Button size="sm" variant="outline-primary" onClick={addInsight}>
            <FaPlus className="me-1" />
            Add insight
          </Button>
        </div>

        {(formData.config.insights || []).map((insight, index) => (
          <Card key={`insight-${index}`} className="mb-2">
            <Card.Body>
              <Row className="align-items-end">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={standardLabelStyles}>Name</Form.Label>
                    <Form.Control
                      value={insight.name || ''}
                      onChange={(e) => updateInsight(index, { name: e.target.value })}
                      style={standardInputStyles}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label style={standardLabelStyles}>Tracker</Form.Label>
                    <Form.Select
                      value={insight.trackerId || ''}
                      onChange={(e) => updateInsight(index, { trackerId: e.target.value })}
                      style={standardInputStyles}
                    >
                      <option value="">Select tracker</option>
                      {trackerIds.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label style={standardLabelStyles}>Days</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={insight.days ?? 0}
                      onChange={(e) =>
                        updateInsight(index, { days: Number(e.target.value) })
                      }
                      style={standardInputStyles}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label style={standardLabelStyles}>Severity</Form.Label>
                    <Form.Select
                      value={insight.severity || 'MEDIUM'}
                      onChange={(e) => updateInsight(index, { severity: e.target.value })}
                      style={standardInputStyles}
                    >
                      {SEVERITY_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeInsight(index)}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>

              <InsightActionsEditor
                actions={insight.actions || []}
                objectName={formData.object_name}
                onChange={(actions) => updateInsight(index, { actions })}
                disabled={!formData.object_name}
              />
            </Card.Body>
          </Card>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          <FaTimes className="me-1" />
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-2" /> : <FaSave className="me-1" />}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
